import { cn } from '@/lib/utils';
import { formatServerDateShort } from '@/lib/dates';

/**
 * Horizontal "run identity" strip rendered at the top of every agent-run
 * surface (predicate, strategy, future agent pages). Mirrors the legacy
 * `.run-card` block in `static/css/agent-progress.css`.
 *
 * Generic over agent kind — takes the minimal shape every agent-run row
 * exposes (id + started_at + status). The agent-specific result page wires
 * it up with its own polling hook's data.
 */

export type AgentRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface AgentRunIdentity {
  id: number;
  /** Customer-facing "Run #N" — per (product, agent). Preferred over the global
   *  `id` for display. Null only on legacy rows before backfill (falls back to id). */
  run_number?: number | null;
  started_at: string | null;
  status: AgentRunStatus;
  /** Optional — when present, the card renders a "Product" group between
   *  Started and Status so the user can see WHICH product is running
   *  without leaving the meta strip. Agents that don't yet plumb
   *  ``product_name`` through their GET endpoint (predicate / strategy
   *  today) leave this undefined; the card collapses the column. */
  product_name?: string | null;
}

/** "Run #NNNN": the per-scope run_number, falling back to the global id. */
export function formatRunNumber(run: { id: number; run_number?: number | null }): string {
  return `#${String(run.run_number ?? run.id ?? 0).padStart(4, '0')}`;
}

export function AgentRunCard({
  run,
  agentLabel = 'run',
}: {
  run: AgentRunIdentity;
  /** Used in aria-label only — "Predicate run #0001" / "Strategy run #0001". */
  agentLabel?: string;
}) {
  // Show the per-(product, agent) number; fall back to the global id only for
  // legacy rows that predate the backfill.
  const idText = formatRunNumber(run);
  const startedAt = run.started_at ? formatServerDateShort(run.started_at) : '—';
  const statusInfo = STATUS_DISPLAY[run.status] ?? { label: run.status, cls: 'running' };
  const productName = run.product_name?.trim() || null;

  return (
    <div
      role="group"
      aria-label={
        productName
          ? `${agentLabel} ${idText} for ${productName}, status ${statusInfo.label}`
          : `${agentLabel} ${idText}, status ${statusInfo.label}`
      }
      className="
        flex items-center gap-7 flex-wrap
        py-[18px] px-6 mb-[22px]
        bg-white border border-line rounded-2xl
        shadow-[0_2px_10px_rgba(20,40,70,0.04)]
        [font-variant-numeric:tabular-nums]
      "
    >
      <Group>
        <Label>Run</Label>
        <span className="font-serif text-2xl font-semibold text-navy leading-none -tracking-[0.02em] [font-variant-numeric:tabular-nums]">
          {idText}
        </span>
      </Group>

      <Divider />

      <Group>
        <Label>Started</Label>
        <span className="font-sans text-base font-medium text-ink leading-[1.2]">
          {startedAt}
        </span>
      </Group>

      {productName && (
        <>
          <Divider />
          <Group className="min-w-0 flex-1">
            <Label>Product</Label>
            <span
              className="font-sans text-base font-medium text-ink leading-[1.2] truncate max-w-[420px]"
              title={productName}
            >
              {productName}
            </span>
          </Group>
        </>
      )}

      <Group className={cn(!productName && 'ml-auto', 'items-end')}>
        <Label>Status</Label>
        <StatusPill cls={statusInfo.cls}>{statusInfo.label}</StatusPill>
      </Group>
    </div>
  );
}

function Group({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-1.5 min-w-0', className)}>{children}</div>;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-bold text-muted uppercase tracking-[0.16em] leading-none">
      {children}
    </span>
  );
}

function Divider() {
  return <div className="self-stretch w-px bg-line my-1" aria-hidden="true" />;
}

function StatusPill({ cls, children }: { cls: StatusCls; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 py-1.5 pr-[14px] pl-[11px] rounded-full text-sm font-bold leading-none tracking-[0.02em] border',
        STATUS_PILL_CLASSES[cls],
      )}
    >
      <span
        className={cn('size-2 rounded-full shrink-0', STATUS_DOT_CLASSES[cls])}
        style={cls === 'running' ? { animation: 'var(--animate-run-dot-pulse)' } : undefined}
        aria-hidden="true"
      />
      <span>{children}</span>
    </span>
  );
}

type StatusCls = 'running' | 'completed' | 'failed';

const STATUS_DISPLAY: Record<AgentRunStatus, { label: string; cls: StatusCls }> = {
  queued: { label: 'Queued', cls: 'running' },
  running: { label: 'Running', cls: 'running' },
  completed: { label: 'Completed', cls: 'completed' },
  failed: { label: 'Failed', cls: 'failed' },
};

const STATUS_PILL_CLASSES: Record<StatusCls, string> = {
  running: 'bg-state-warning-bg text-state-warning-text border-state-warning-border',
  completed: 'bg-state-success-bg text-state-success-text border-state-success-border',
  failed: 'bg-state-error-bg text-state-error-text border-state-error-border',
};

const STATUS_DOT_CLASSES: Record<StatusCls, string> = {
  running: 'bg-state-warning-accent',
  completed: 'bg-state-success-accent',
  failed: 'bg-state-error-accent',
};
